import { SearchIcon } from '../../components/icons/SearchIcon';
import { TextField } from '../../components/inputs/TextField';
import { useEffect, useMemo, useState } from 'react';
import clsx from 'clsx';
import Pager from '../../components/Pager';
import { closeSeeAll } from '../accordion/accordionSlice';
import { useFilteredProductQuery } from '../api/useFilteredProduct';
import {
  selectProductTag,
  selectStyle,
  updateStyle
} from '../item/store/itemSlice';
import { Group, Style } from '../../types';
import { useConfiguratorDispatch, useRootSelector } from '../../store';
import GridElement from '../../components/grids/GridElement';
import SeeAllGrid from '../../components/grids/SeeAllGrid';

const DEFAULT_STYLES_TO_DISPLAY = 12;

const StyleSkeleton = (
  <ol
    role="status"
    className="grid animate-pulse grid-cols-3 grid-rows-[66px_66px_66px_66px] items-center gap-2.5 pb-2.5"
  >
    {Array.from({ length: DEFAULT_STYLES_TO_DISPLAY }).map((_, i) => (
      <li
        key={i}
        className="rounded-md border-2 border-solid border-transparent"
      >
        <div className="m-px flex rounded-md border border-solid border-gray-100 bg-gray-100">
          <div className="box-content h-[60px] w-[60px] rounded bg-gray-200 object-scale-down object-center" />
          <div></div>
        </div>
      </li>
    ))}
  </ol>
);

export default function AllStyles() {
  const dispatch = useConfiguratorDispatch();

  const productTag = useRootSelector(selectProductTag);

  const [stylesPage, setStylesPage] = useState(0);
  const [filter, setFilter] = useState('');
  const [totalPages, setTotalPages] = useState(0);
  const selectedStyle = useRootSelector(selectStyle);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const outerSelectedStyle = useMemo(() => selectedStyle, []);

  const [styleSlice, setStyleSlice] = useState<Style[]>([]);

  const { data, isLoading, isSuccess, isError, error } =
    useFilteredProductQuery(productTag);

  const styles = useMemo(() => {
    return data?.styles ?? [];
  }, [data?.styles]);

  const groups = useMemo(() => {
    let permittedGroups: Group[] = [];
    if (data) {
      permittedGroups = data?.filters?.filter(f => f.type === 'styles');
    }
    if (permittedGroups?.length) {
      return [
        { display_name: 'All', group_id: 'all' },
        ...permittedGroups
      ] as Group[];
    } else {
      return null;
    }
  }, [data]);
  const [selectedGroup, setSelectedGroup] = useState<Group>();

  useEffect(() => {
    if (groups?.length) {
      setSelectedGroup(groups[0]);
    }
  }, [groups]);

  useEffect(() => {
    let groupedStyles;
    if (selectedGroup?.entity_ids) {
      groupedStyles = styles.filter(s =>
        selectedGroup.entity_ids.includes(s.style_id)
      );
    } else {
      groupedStyles = styles;
    }
    const filteredStyles = groupedStyles.filter(s =>
      s.display_name.toLowerCase().includes(filter.toLowerCase())
    );
    const newTotalPages = Math.ceil(
      filteredStyles?.length / DEFAULT_STYLES_TO_DISPLAY
    );
    setTotalPages(newTotalPages);
    const newStylesPages = Math.min(Math.max(0, newTotalPages - 1), stylesPage);
    setStylesPage(newStylesPages);
    const start = newStylesPages * DEFAULT_STYLES_TO_DISPLAY;
    const end = start + DEFAULT_STYLES_TO_DISPLAY;
    setStyleSlice(filteredStyles?.slice(start, end) ?? []);
  }, [styles, stylesPage, filter, selectedGroup?.entity_ids]);

  return (
    <>
      <div className="my-2.5 flex flex-col gap-2.5 overflow-y-auto">
        <div className="px-2.5 py-1 text-center text-sm font-semibold">
          Select a Style
        </div>
        <ul className="flex flex-wrap gap-1.5">
          {groups?.map(group => (
            <li
              key={group.group_id}
              onClick={() => setSelectedGroup(group)}
              className={clsx(
                'rounded border border-solid px-2 py-1.5 transition-all',
                {
                  'border-accent bg-accent text-accent-contrast':
                    selectedGroup == group,
                  'border-secondary-button-text bg-secondary-button text-secondary-button-text':
                    selectedGroup != group
                }
              )}
            >
              {group.display_name}
            </li>
          ))}
        </ul>
        <TextField
          size="large"
          className="flex-1"
          placeholder="Search"
          contentAfter={<SearchIcon />}
          value={filter}
          onChange={e => setFilter(e.target.value)}
        />
        {isLoading && StyleSkeleton}
        {isError && (
          <>
            <h1>There was an error loading colors</h1> {console.log(error)}
          </>
        )}
        {isSuccess && (
          <SeeAllGrid>
            {styleSlice.map(style => (
              <GridElement
                key={style.style_id}
                display_name={style.display_name}
                thumbnail_path={style.thumbnail_path}
                selected={style == selectedStyle}
                onClick={() => dispatch(updateStyle(style))}
              />
            ))}
          </SeeAllGrid>
        )}
        <Pager
          size="large"
          totalPages={totalPages}
          currentPage={stylesPage}
          onBack={() => setStylesPage(old => Math.max(0, old - 1))}
          onForward={() =>
            setStylesPage(old => Math.min(old + 1, totalPages - 1))
          }
        />
        <div className="flex justify-stretch gap-2">
          <button
            className="border-secondary-button-text bg-secondary-button text-secondary-button-text grow rounded-full border border-solid px-2.5 py-4 font-semibold transition-all hover:brightness-95"
            onClick={() => {
              outerSelectedStyle && dispatch(updateStyle(outerSelectedStyle));
              dispatch(closeSeeAll());
            }}
          >
            Back
          </button>
          <button
            onClick={() => dispatch(closeSeeAll())}
            className="bg-primary-button text-primary-button-text grow rounded-full border border-transparent px-2.5 py-4 font-semibold transition-all hover:brightness-95"
          >
            Apply
          </button>
        </div>
      </div>
    </>
  );
}
